<template>
  <div
    :class="[
      `design-system-input flex w-full cursor-text items-center rounded-lg border transition`,
      `outline-2 -outline-offset-1 has-[:focus-visible]:outline`,
      isDisabled && `opacity-50`,
      fieldContext?.validationStatus.value === 'error'
        ? `border-red-300 outline-red-500 dark:border-red-800`
        : `outline-blue-500`,
    ]"
    @keydown="handleKeydown"
  >
    <DateSegments
      ref="segments"
      v-model="modelValue"
      :disabled="isDisabled"
      class="p-1.5"
      @focus-after-last="focusCalendarButton"
    />

    <PopoverRoot v-model:open="isOpen">
      <PopoverTrigger
        ref="calendar"
        :disabled="isDisabled"
        aria-label="Calendar"
        class="mx-1 shrink-0 rounded-md p-1.5 text-muted-foreground outline-2 -outline-offset-1 outline-blue-500 transition hover:text-foreground focus-visible:outline disabled:cursor-not-allowed"
        @keydown="handleCalendarButtonKeydown"
      >
        <CalendarIcon class="size-4" />
      </PopoverTrigger>

      <PopoverPortal>
        <Transition
          enter-active-class="transition-opacity duration-100 ease-out"
          enter-from-class="opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="transition duration-75 ease-in"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <PopoverContent
            :side-offset="8"
            :collision-padding="10"
            class="z-popover rounded-lg border border-border bg-background shadow-xl"
          >
            <DateCalendar
              v-model="modelValue"
              :min="min"
              :max="max"
              autofocus
              @select="handleCalendarSelect"
            />
          </PopoverContent>
        </Transition>
      </PopoverPortal>
    </PopoverRoot>
  </div>
</template>

<script setup lang="ts">
import {
  PopoverContent,
  PopoverPortal,
  PopoverRoot,
  PopoverTrigger,
} from "reka-ui";
import { computed, ref, useTemplateRef } from "vue";

import CalendarIcon from "@/icons/heroicons/calendar-micro.svg";

import { injectFieldContext } from "./context";
import DateCalendar from "./internal/DateCalendar.vue";
import DateSegments from "./internal/DateSegments.vue";

const modelValue = defineModel<string>({ default: "" });

const props = defineProps<{
  disabled?: boolean;
  min?: string;
  max?: string;
}>();

const fieldContext = injectFieldContext(null);

const isDisabled = computed(() => {
  return fieldContext?.disabled.value || props.disabled;
});

const isOpen = ref(false);

const segments = useTemplateRef("segments");
const calendar = useTemplateRef("calendar");

const focusCalendarButton = (): void => {
  calendar.value?.$el?.focus();
};

const handleKeydown = (event: KeyboardEvent): void => {
  if (isDisabled.value) {
    return;
  }

  // Alt+Arrow opens calendar
  if (event.altKey && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
    event.preventDefault();
    isOpen.value = true;
  }
};

const handleCalendarButtonKeydown = (event: KeyboardEvent): void => {
  if (isDisabled.value) return;

  // Alt+Arrow opens calendar
  if (event.altKey && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
    event.preventDefault();
    isOpen.value = true;
    return;
  }

  if (event.key === "ArrowLeft") {
    event.preventDefault();
    segments.value?.focusLastSegment();
  }
};

const handleCalendarSelect = (): void => {
  isOpen.value = false;
};
</script>
